import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { blacklistEntries } from "@/mocks/blacklist";
import type { RiskEntry } from "@/types/content";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import RiskRecordCard from "@/components/feature/RiskRecordCard";
import FilterBar from "./components/FilterBar";

const SITE = "https://imgmmd.cn";
const PAGE_SIZE = 6;

export default function Blacklist() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "all";
  const level = searchParams.get("level") || "all";
  const sort = searchParams.get("sort") || "updated";
  const urlKeyword = searchParams.get("q") || "";

  const [keyword, setKeyword] = useState(urlKeyword);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setKeyword(urlKeyword);
  }, [urlKeyword]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [category, level, sort, urlKeyword]);

  const filtered = useMemo(() => {
    const query = urlKeyword.trim().toLowerCase();
    let list = (blacklistEntries as RiskEntry[]).filter((entry) => {
      const matchCategory = category === "all" || entry.categoryKey === category;
      const matchLevel = level === "all" || entry.riskLevel === level;
      const haystack = [entry.name, entry.domain, entry.category, ...entry.tags, entry.summary]
        .join(" ")
        .toLowerCase();
      const matchKeyword = !query || haystack.includes(query);
      return matchCategory && matchLevel && matchKeyword;
    });

    list = [...list].sort((a, b) => {
      if (sort === "complaints") return b.complaintCount - a.complaintCount;
      if (sort === "views") return b.views - a.views;
      if (sort === "name") return a.name.localeCompare(b.name, "zh-Hans-CN");
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });

    return list;
  }, [category, level, sort, urlKeyword]);

  const visible = filtered.slice(0, visibleCount);

  const updateParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === "all") {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    setSearchParams(next, { replace: true });
  };

  const handleSearchSubmit = () => {
    updateParams({ q: keyword.trim() });
  };

  const resetFilters = () => {
    setKeyword("");
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "避雷名单：网站黑料与不靠谱平台记录",
      url: `${SITE}/blacklist`,
      inLanguage: "zh-CN",
      description:
        "按风险类型与等级整理的网站黑料、不靠谱平台、诈骗举报与跑路记录名单，支持关键词检索与筛选。",
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: filtered.length,
        itemListElement: filtered.slice(0, 10).map((entry, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${SITE}/blacklist/${entry.id}`,
          name: entry.name,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "避雷名单", item: `${SITE}/blacklist` },
      ],
    },
  ];

  return (
    <>
      <PageMeta
        title="避雷名单_不靠谱平台与网站黑料查询 - 避雷哨"
        description="按风险类型与等级检索避雷名单，查看不靠谱平台、诈骗举报、跑路记录与投诉汇总。支持关键词搜索、分类切换与结果排序。"
        keywords="避雷名单,网站黑料,不靠谱平台,跑路记录,诈骗举报"
        path="/blacklist"
        jsonLd={jsonLd}
      />

      <section className="border-b border-background-200 bg-gradient-to-b from-background-100 to-background-50">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ name: "避雷名单" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            避雷名单：网站黑料与不靠谱平台记录
          </h1>
          <p className="mt-3 max-w-3xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
            这里的每一条记录都来自用户提交与公开信息整理。你可以按风险类型或等级筛选，也可以直接搜索平台名称与域名。查询结果仅供参考，交易前请结合实际情况判断。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
        <FilterBar
          keyword={keyword}
          category={category}
          level={level}
          sort={sort}
          resultCount={filtered.length}
          onKeywordChange={setKeyword}
          onSearchSubmit={handleSearchSubmit}
          onCategoryChange={(value) => updateParams({ category: value })}
          onLevelChange={(value) => updateParams({ level: value })}
          onSortChange={(value) => updateParams({ sort: value })}
        />

        {visible.length > 0 ? (
          <>
            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {visible.map((entry) => (
                <RiskRecordCard key={entry.id} entry={entry} />
              ))}
            </div>

            {visibleCount < filtered.length && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 font-label text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                >
                  <i className="ri-add-line text-base" />
                  加载更多记录（剩余 {filtered.length - visibleCount} 条）
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-file-search-line text-2xl" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有匹配的风险记录</h2>
            <p className="mt-2 max-w-md font-body text-sm leading-relaxed text-foreground-600">
              换个关键词试试，或者放宽筛选条件。如果你掌握相关线索，也欢迎提交给我们核实收录。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置筛选条件
            </button>
          </div>
        )}
      </section>
    </>
  );
}